import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { Checkbox, Modal } from "@material-ui/core";
import theme from "../../../assets/theme";
import COLORS from "../../../assets/Colors";
import { useSnackbar } from "notistack";
import useLoader from "../../../hooks/useLoader";
import {
  PrimaryCTAButton,
  PrimaryOutlinedCloseCTAButton,
} from "../../common/Buttons";
import { getErrorMessage } from "../../../helpers/functions";
import useBundle from "../../../hooks/useBundle";
import { CustomInputField } from "../../common/FormInputs";
import CaseService from "../../../services/CaseService";
import { Formik } from "formik";
import { GenerateBunldeSchema } from "../../../helpers/validationSchema";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import pdfWorker from "pdfjs-dist/legacy/build/pdf.worker.entry";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

export default function Index({
  openBundleModal,
  setOpenBundleModal,
  id,
  caseId,
  bundleName,
  docName,
  setRefreshDetails,
  bundleDocs,
  setDocName,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { setLoader } = useLoader();
  const [docsInfo, setDocsInfo] = useState(bundleDocs);
  const { setBundleOpen } = useBundle();
  const [totalPage, setTotalPage] = useState(0);
  const [bundleError, setBundleError] = useState(null);
  const [errorOpen, setErrorOpen] = useState(false);

  useEffect(() => {
    if (!docName?.length) return;

    const loadAllPages = async () => {
      const updatedDocs = await Promise.all(
        docName.map(async (doc) => {
          if (doc?.totalPages) return doc;

          try {
            const pages = await getPdfPages(doc?.url?.url);
            return { ...doc, totalPages: pages };
          } catch {
            return { ...doc, totalPages: 0 };
          }
        }),
      );

      setDocName(updatedDocs);
    };
    loadAllPages(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openBundleModal]);

  async function onFormSubmit(values, { resetForm }) {
    try {
      setLoader({
        state: true,
        message: "Your bundle is being generated. Please wait...",
      });

      const payload = {
        bundleName,
        bundleDocuments: docsInfo,
        addPageNumber: values?.addPageNumber,
      };

      const res = await CaseService.updateBundle(payload, caseId, id);

      const msg = res?.message;

      const isObjectMessage =
        typeof msg === "object" && msg !== null && !Array.isArray(msg);

      if (isObjectMessage) {
        setBundleError(
          Object.keys(msg).length > 0
            ? msg
            : { general: ["Bundle generation failed. Please try again."] },
        );

        setErrorOpen(true);
        return;
      }

      setBundleError(null);
      enqueueSnackbar(
        "Document is being generated. Large files may take a few minutes to process. Please refresh in a few minutes.",
        { variant: "success" },
      );
      setRefreshDetails(true);
      resetForm();
      setOpenBundleModal(false);
      setBundleOpen({ state: false, boolean: false });
    } catch (error) {
      const message = getErrorMessage(error);
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setLoader({ state: false });
    }
  }

  const getPdfPages = async (url) => {
    try {
      const loadingTask = pdfjsLib.getDocument(url);
      const pdf = await loadingTask.promise;
      return pdf.numPages;
    } catch (error) {
      console.error("PDF read error:", error);
      return 0;
    }
  };

  const handleSelect = (event, index, setFieldValue) => {
    const checked = event.target.checked;
    const updatedDocs = [...docName];

    const total = updatedDocs[index]?.totalPages || 0;

    updatedDocs[index] = {
      ...updatedDocs[index],
      checked,
      pageNo: checked && total ? `1-${total}` : "",
    };
    setDocName(updatedDocs);
    // const selectedDocs = updatedDocs.filter(
    // (doc) => doc.checked && doc.visibilityType !== "me",
    // );

    const selectedDocs = updatedDocs.filter(
      (doc) =>
        doc.checked &&
        doc.visibilityType !== "me" &&
        doc.visibilityType !== "others",
    );

    setDocsInfo(selectedDocs);

    const totalCount = selectedDocs.reduce(
      (sum, doc) => sum + (doc.totalPages || 0),
      0,
    );

    setTotalPage(totalCount);
    setFieldValue("pageNumber", totalCount);
  };

  const handleDocChange = (event, index, pageNo, setFieldValue) => {
    const value = event.target.value;

    const updatedDocs = [...docName];

    updatedDocs[index] = {
      ...updatedDocs[index],
      [pageNo]: value,
      checked: value.trim() !== "",
    };

    setDocName(updatedDocs);

    // const selectedDocs = updatedDocs.filter(
    // (doc) => doc.checked && doc.visibilityType !== "me",
    // );

    const selectedDocs = updatedDocs.filter(
      (doc) =>
        doc.checked &&
        doc.visibilityType !== "me" &&
        doc.visibilityType !== "others",
    );

    setDocsInfo(selectedDocs);

    let totalCount = 0;

    selectedDocs.forEach((doc) => {
      if (doc.pageNo === "All") {
        totalCount += doc.totalPages || 0;
      } else if (doc.pageNo) {
        const parts = doc.pageNo.split(",");

        parts.forEach((p) => {
          if (p.includes("-")) {
            const [start, end] = p.split("-").map(Number);

            if (!isNaN(start) && !isNaN(end)) {
              const min = Math.min(start, end);
              const max = Math.max(start, end);

              totalCount += max - min + 1;
            }
          } else {
            const num = Number(p);
            if (!isNaN(num)) totalCount += 1;
          }
        });
      }
    });

    setTotalPage(totalCount);
    setFieldValue("pageNumber", totalCount);
  };

  // const filteredDocs = docName?.filter((doc) => doc.visibilityType !== "me");

  const filteredDocs = docName?.filter(
    (doc) => doc.visibilityType !== "me" && doc.visibilityType !== "others",
  );

  const selectedCount = filteredDocs?.filter((d) => d?.checked)?.length || 0;

  /* ── Error modal ─────────────────────────────────── */
  if (bundleError && Object.keys(bundleError).length > 0) {
    return (
      <Modal
        open={errorOpen}
        onClose={() => {
          setErrorOpen(false);
          // setOpenBundleModal(false);
          setBundleError(null);
        }}
        disableRestoreFocus
      >
        <Overlay>
          <ModalCard role="presentation" style={{ maxWidth: 480 }}>
            <ModalHeader>
              <HeaderIconWrap error>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              </HeaderIconWrap>
              <HeaderText>
                <ModalTitle>Generation Errors</ModalTitle>
                <ModalSubtitle>
                  Please resolve these issues and try again
                </ModalSubtitle>
              </HeaderText>
              <CloseBtn
                onClick={() => {
                  setErrorOpen(false);
                  // setOpenBundleModal(false);
                  setBundleError(null);
                }}
                aria-label="Close"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </CloseBtn>
            </ModalHeader>

            <ModalDivider />

            <ErrorScrollArea>
              {Object.entries(bundleError).map(([fileName, errors]) => (
                <ErrorBlock key={fileName}>
                  <ErrorFileName>
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                    >
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    </svg>
                    {fileName}
                  </ErrorFileName>
                  <ErrorList>
                    {errors.map((err, i) => (
                      <ErrorListItem key={i}>
                        <ErrorDot />
                        {err}
                      </ErrorListItem>
                    ))}
                  </ErrorList>
                </ErrorBlock>
              ))}
            </ErrorScrollArea>

            <ModalDivider />
            <ModalFooter>
              <PrimaryOutlinedCloseCTAButton
                style={{ flex: 1 }}
                onClick={() => {
                  setErrorOpen(false);
                  // setOpenBundleModal(false);
                  setBundleError(null);
                }}
              >
                Close
              </PrimaryOutlinedCloseCTAButton>
            </ModalFooter>
          </ModalCard>
        </Overlay>
      </Modal>
    );
  }

  return (
    <Modal
      open={openBundleModal}
      onClose={() => {
        setOpenBundleModal(false);
        setBundleOpen({ state: false, boolean: false });
      }}
      disableRestoreFocus={true}
    >
      <Overlay>
        <ModalCard role="presentation">
          <ModalHeader>
            <HeaderText>
              <ModalTitle>Generate Bundle</ModalTitle>
              <ModalSubtitle>
                Select documents and configure options
              </ModalSubtitle>
            </HeaderText>
            <CloseBtn
              onClick={() => {
                setOpenBundleModal(false);
                setBundleOpen({ state: false, boolean: false });
              }}
              aria-label="Close"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </CloseBtn>
          </ModalHeader>

          <ModalDivider />

          <Formik
            initialValues={{
              addPageNumber: false,
              pageNumber: totalPage || 0,
            }}
            validationSchema={GenerateBunldeSchema}
            enableReinitialize
            validateOnChange
            validateOnBlur
            onSubmit={onFormSubmit}
          >
            {({ values, errors, touched, setFieldValue, handleSubmit }) => (
              <form
                onSubmit={handleSubmit}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  flex: 1,
                  overflow: "hidden",
                }}
              >
                {/* Stats Bar */}
                <StatsBar>
                  <StatItem>
                    <StatNum>{filteredDocs?.length || 0}</StatNum>
                    <StatLabel>Total Documents</StatLabel>
                  </StatItem>
                  <StatDivider />
                  <StatItem>
                    <StatNum style={{ color: "#00838c" }}>
                      {selectedCount}
                    </StatNum>
                    <StatLabel>Selected</StatLabel>
                  </StatItem>
                  <StatDivider />
                  <StatItem>
                    <StatNum>{totalPage}</StatNum>
                    <StatLabel>Total Pages</StatLabel>
                  </StatItem>
                </StatsBar>

                {/* Document List */}
                <SectionLabel>
                  Documents
                  {errors?.pageNumber && (
                    <ErrorBadge>{errors.pageNumber}</ErrorBadge>
                  )}
                </SectionLabel>

                <DocumentScroll docCount={filteredDocs?.length}>
                  {filteredDocs?.map((item) => {
                    const originalIndex = docName.findIndex(
                      (d) => d.name === item.name,
                    );
                    return (
                      <DocumentRow
                        key={item.name + originalIndex}
                        checked={item?.checked}
                      >
                        <RowLeft>
                          <CustomCheckbox
                            onChange={(ev) =>
                              handleSelect(ev, originalIndex, setFieldValue)
                            }
                            checked={item?.checked}
                            name={item?.name + originalIndex}
                          />
                          <DocInfo>
                            <DocIcon>{"📄"}</DocIcon>
                            <DocName checked={item?.checked}>
                              {item.name}
                            </DocName>
                          </DocInfo>
                        </RowLeft>
                        <PageInput className="bundle-doc">
                          <PageLabel>Pages</PageLabel>
                          <CustomInputField
                            className="input-white"
                            variant="outlined"
                            value={item?.checked ? item?.pageNo : ""}
                            disabled={false}
                            placeholder={
                              item?.totalPages ? `1-${item.totalPages}` : "1-0"
                            }
                            onChange={(event) =>
                              handleDocChange(
                                event,
                                originalIndex,
                                "pageNo",
                                setFieldValue,
                              )
                            }
                          />
                        </PageInput>
                      </DocumentRow>
                    );
                  })}
                </DocumentScroll>

                {/* Options */}
                <OptionsSection>
                  <OptionsSectionLabel>Bundle Options</OptionsSectionLabel>
                  <OptionToggle>
                    <CustomCheckbox
                      name="addPageNumber"
                      value={values.addPageNumber}
                      checked={values.addPageNumber}
                      onChange={() =>
                        setFieldValue("addPageNumber", !values.addPageNumber)
                      }
                    />
                    <OptionContent>
                      <OptionTitle>Add Page Numbers</OptionTitle>
                      <OptionDesc>
                        Automatically number all pages in the generated bundle
                      </OptionDesc>
                    </OptionContent>
                    <ToggleIndicator active={values.addPageNumber}>
                      {values.addPageNumber ? "ON" : "OFF"}
                    </ToggleIndicator>
                  </OptionToggle>
                </OptionsSection>

                <ModalDivider style={{ margin: "0 24px" }} />

                <ModalFooter>
                  <PrimaryOutlinedCloseCTAButton
                    style={{ flex: 1 }}
                    onClick={() => {
                      setOpenBundleModal(false);
                      setBundleOpen({ state: false, boolean: false });
                    }}
                  >
                    Cancel
                  </PrimaryOutlinedCloseCTAButton>
                  <PrimaryCTAButton
                    style={{ flex: 1 }}
                    disabled={!docsInfo?.length}
                    onClick={handleSubmit}
                  >
                    Generate Bundle
                    {selectedCount > 0 && (
                      <CountBadge>{selectedCount}</CountBadge>
                    )}
                  </PrimaryCTAButton>
                </ModalFooter>
              </form>
            )}
          </Formik>
        </ModalCard>
      </Overlay>
    </Modal>
  );
}

const CustomCheckbox = ({ ...props }) => (
  <Checkbox
    color="primary"
    icon={
      <img src={require("../../../assets/images/checkbox.svg")} alt="check" />
    }
    checkedIcon={
      <img
        src={require("../../../assets/images/checkBoxSelected.svg")}
        alt="check"
      />
    }
    {...props}
  />
);

const fadeSlideIn = keyframes`
 from { opacity: 0; transform: translate(-50%, -48%) scale(0.97); }
 to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
`;

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
`;

export const ModalCard = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: calc(100% - 32px);
  max-width: 560px;
  background: #ffffff;
  border-radius: 20px;
  box-shadow:
    0 24px 60px rgba(0, 0, 0, 0.18),
    0 8px 24px rgba(0, 131, 140, 0.1);
  animation: ${fadeSlideIn} 0.25s ease;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  overflow: hidden;
`;

export const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 24px 24px 20px;
  flex-shrink: 0;
`;

export const HeaderText = styled.div`
  flex: 1;
`;

export const ModalTitle = styled.h2`
  margin: 0;
  font-family: ${theme.fonts.primaryFontExtraBold};
  font-size: 18px;
  color: #293461;
  line-height: 1.2;
`;

export const ModalSubtitle = styled.p`
  margin: 4px 0 0;
  font-size: 13px;
  color: #64748b;
  font-family: ${theme.fonts.primaryFontRegular};
`;

export const CloseBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  transition: all 0.2s;
  flex-shrink: 0;
  &:hover {
    background: #fee2e2;
    border-color: #fca5a5;
    color: #ef4444;
  }
`;

export const ModalDivider = styled.div`
  height: 1px;
  background: linear-gradient(90deg, transparent, #e2e8f0, transparent);
  margin: 0 24px;
  flex-shrink: 0;
`;

export const StatsBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  padding: 16px 24px;
  background: #f8fafc;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
`;

export const StatItem = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
`;

export const StatNum = styled.span`
  font-family: ${theme.fonts.primaryFontExtraBold};
  font-size: 22px;
  color: #293461;
  line-height: 1;
`;

export const StatLabel = styled.span`
  font-size: 11px;
  color: #94a3b8;
  font-family: ${theme.fonts.primaryFontRegular};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const StatDivider = styled.div`
  width: 1px;
  height: 36px;
  background: #e2e8f0;
`;

export const SectionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 24px 10px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  color: #94a3b8;
  flex-shrink: 0;
`;

export const ErrorBadge = styled.span`
  font-size: 11px;
  color: #ef4444;
  background: #fee2e2;
  padding: 2px 8px;
  border-radius: 100px;
  text-transform: none;
  letter-spacing: 0;
`;

export const DocumentScroll = styled.div`
  overflow-y: auto;
  max-height: ${({ docCount }) => (docCount > 3 ? "280px" : "auto")};
  padding: 0 24px 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const DocumentRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px;
  border-radius: 12px;
  border: 1.5px solid ${({ checked }) => (checked ? "#99f6e4" : "#e2e8f0")};
  background: ${({ checked }) => (checked ? "#f0fdfb" : "#fafafa")};
  transition: all 0.2s;

  @media (max-width: 500px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const RowLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

export const DocInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

export const DocIcon = styled.span`
  font-size: 16px;
  flex-shrink: 0;
`;

export const DocName = styled.span`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 13px;
  color: ${({ checked }) => (checked ? "#0f766e" : "#64748b")};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const PageInput = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 120px;
  flex-shrink: 0;

  @media (max-width: 500px) {
    width: 100%;
  }
`;

export const PageLabel = styled.span`
  font-size: 10px;
  color: #94a3b8;
  font-family: ${theme.fonts.primaryFontSemiBold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const OptionsSection = styled.div`
  padding: 14px 24px 16px;
  flex-shrink: 0;
`;

export const OptionsSectionLabel = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  color: #94a3b8;
  margin-bottom: 10px;
`;

export const OptionToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  background: #fafafa;
  transition: border-color 0.2s;
  &:hover {
    border-color: #00838c;
  }
`;

export const OptionContent = styled.div`
  flex: 1;
`;

export const OptionTitle = styled.p`
  margin: 0;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 14px;
  color: #293461;
`;

export const OptionDesc = styled.p`
  margin: 2px 0 0;
  font-size: 12px;
  color: #94a3b8;
  font-family: ${theme.fonts.primaryFontRegular};
`;

export const ToggleIndicator = styled.span`
  font-size: 11px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  padding: 3px 10px;
  border-radius: 100px;
  background: ${({ active }) => (active ? "#d1fae5" : "#f1f5f9")};
  color: ${({ active }) => (active ? "#059669" : "#94a3b8")};
  letter-spacing: 0.5px;
  transition: all 0.2s;
`;

export const ModalFooter = styled.div`
  display: flex;
  gap: 12px;
  padding: 16px 24px 24px;
  flex-shrink: 0;
`;

export const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  font-size: 11px;
  margin-left: 8px;
`;

export const Label = styled.div`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 10px;
  text-transform: capitalize;
  font-weight: 600;
  line-height: 1.7;
  margin-bottom: 6px;
  color: ${COLORS.COLOR_DARK};
  text-align: left;
`;

/* ─── Error Modal Specifics ─────────────────────────────────────── */

const HeaderIconWrap = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${({ error }) =>
    error ? "rgba(239,68,68,0.1)" : "rgba(41,52,97,0.08)"};
  color: ${({ error }) => (error ? "#ef4444" : "#293461")};
`;

const ErrorScrollArea = styled.div`
  padding: 16px 22px;
  overflow-y: auto;
  max-height: 260px;
  display: flex;
  flex-direction: column;
  gap: 10px;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #e1e3ee;
    border-radius: 2px;
  }
`;

const ErrorBlock = styled.div`
  background: #fff5f5;
  border: 1.5px solid #fecaca;
  border-radius: 10px;
  padding: 12px 14px;
`;

const ErrorFileName = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 13px;
  color: #b91c1c;
  margin-bottom: 8px;
`;

const ErrorList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const ErrorListItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 12.5px;
  color: #7f1d1d;
  font-family: ${theme.fonts.primaryFontRegular};
  line-height: 1.5;
`;

const ErrorDot = styled.div`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #ef4444;
  flex-shrink: 0;
  margin-top: 6px;
`;
